import { React, useEffect, useState, useRef } from 'react';
import { List, Checkbox, Button, Typography, Row } from 'antd';
const { Text } = Typography;

function formatTime(seconds) {
    let reverse = false
    if (seconds < 0) {
        seconds = -seconds
        reverse = true
    }
    const minutes = Math.floor(seconds / 60); // Get the full minutes
    const remainingSeconds = seconds % 60;    // Get the remaining seconds
    // Return the formatted time string
    // Add a leading zero to seconds if less than 10
    const timeStr = reverse ? `-${minutes}'${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}` : `${minutes}'${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    return timeStr
}

function findLastSelected(steps, index) {
    for (let i = Math.min(index, steps.length - 1); i >= 0; i--) {
        if (steps[i].selected) {
            return i; // Return the index of the last selected step
        }
    }
    return -1; // Return -1 if no selected step is found
}

// Create a translucent variant of an rgba() color
function withAlpha(rgbaColor, alpha) {
    const match = rgbaColor.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9.]+))?\s*\)/i);
    if (!match) return rgbaColor;
    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);
    const a = typeof alpha === 'number' ? alpha : (match[4] ? parseFloat(match[4]) : 1);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function TrainingList({ steps, toggleSelectStep, FTP, reorderSteps, onOpenTrainingForm, fillHeight = false, powerUnit = "watts" }) {
    const [totTimeFinal, setTotTimeFinal] = useState(0);
    const [totWatt, setTotWatt] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [dropTargetIndex, setDropTargetIndex] = useState(null);
    const itemRefs = useRef([]);

    const ftpNum = Number(FTP);
    const ftpSafe = Number.isFinite(ftpNum) && ftpNum > 0 ? ftpNum : 100;

    const getColor = (intensity) => {
        if (intensity >= ftpSafe * 1.5) {
            return 'rgba(128, 0, 128, 1)';
        } else if (intensity >= ftpSafe * 1.19) {
            return 'rgba(255, 0, 0, 1)';
        } else if (intensity >= ftpSafe * 1.05) {
            return 'rgba(255, 165, 0, 1)';
        } else if (intensity >= ftpSafe * 0.9) {
            return 'rgba(255, 215, 0, 1)';
        } else if (intensity >= ftpSafe * 0.75) {
            return 'rgba(0, 128, 0, 1)';
        }
        else if (intensity > ftpSafe * 0.6) {
            return 'rgba(153, 204, 255, 1)';
        } else {
            return 'rgba(128, 128, 128, 1)';
        }
    };
    const [isShiftPressed, setIsShiftPressed] = useState(false);

    useEffect(() => {
        // Event handler for keydown
        const handleKeyDown = (event) => {
            if (event.key === 'Shift') {
                setIsShiftPressed(true);
            }
        };

        // Event handler for keyup
        const handleKeyUp = (event) => {
            if (event.key === 'Shift') {
                setIsShiftPressed(false);
            }
        };

        // Global drag end handler to reset state
        const handleDragEnd = () => {
            setIsDragging(false);
            setDropTargetIndex(null);
        };

        // Add event listeners
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('dragend', handleDragEnd);

        // Cleanup listeners on component unmount
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('dragend', handleDragEnd);
        };
    }, []);

    useEffect(() => {
        setTotTimeFinal(steps.map(s => s.duration).reduce(
            (accumulator, currentValue) => accumulator + currentValue,
            0,
        ));
        setTotWatt(steps.map(s => s.duration * s.watt).reduce(
            (accumulator, currentValue) => accumulator + currentValue,
            0,
        ));
    }, [steps]);

    const getDraggedIndices = (startIndex) => {
        // If the starting item is selected, capture only the contiguous block
        // around it (left and right) of selected items. Ignore other disjoint selections.
        if (steps[startIndex] && steps[startIndex].selected) {
            let left = startIndex;
            let right = startIndex;
            while (left - 1 >= 0 && steps[left - 1].selected) {
                left -= 1;
            }
            while (right + 1 < steps.length && steps[right + 1].selected) {
                right += 1;
            }
            return Array.from({ length: right - left + 1 }, (_, i) => left + i);
        }
        // Otherwise, drag just this single item
        return [startIndex];
    };

    const onItemDragStart = (e, index) => {
        const indices = getDraggedIndices(index);
        e.dataTransfer.effectAllowed = "move";
        try {
            e.dataTransfer.setData("text/plain", JSON.stringify({ indices, type: 'steps' }));
        } catch (_) {
            e.dataTransfer.setData("text", JSON.stringify({ indices, type: 'steps' }));
        }
        setIsDragging(true);
    };

    const onDropZoneDragOver = (e, dropIndex) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDropTargetIndex(dropIndex);
    };

    const onDropZoneDragLeave = () => {
        setDropTargetIndex(null);
    };

    const onDropZoneDrop = (e, dropIndex) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            let payload = e.dataTransfer.getData("text/plain") || e.dataTransfer.getData("text");
            const data = JSON.parse(payload);

            if (data.type === 'steps' && data.indices) {
                const sourceIndices = Array.from(new Set(data.indices)).filter(i => Number.isInteger(i)).sort((a, b) => a - b);

                if (sourceIndices.length > 0 && reorderSteps) {
                    // Calculate destination index accounting for removed items
                    const destIndex = dropIndex - sourceIndices.filter(i => i < dropIndex).length;
                    reorderSteps({ sourceIndices, destinationIndex: Math.max(0, destIndex) });
                }
            }
        } catch (error) {
            console.error('Drop error:', error);
        }

        setIsDragging(false);
        setDropTargetIndex(null);
    };

    const onContainerDragOver = (e) => {
        e.preventDefault();
    };

    const onContainerDrop = (e) => {
        // Drop to the end of the list
        onDropZoneDrop(e, steps.length);
    };

    const DropZone = ({ index, isVisible }) => (
        <div
            onDragOver={(e) => onDropZoneDragOver(e, index)}
            onDragLeave={onDropZoneDragLeave}
            onDrop={(e) => onDropZoneDrop(e, index)}
            style={{
                height: dropTargetIndex === index ? '8px' : '4px',
                backgroundColor: dropTargetIndex === index ? '#1890ff' : 'transparent',
                border: dropTargetIndex === index ? '2px dashed #1890ff' : '2px dashed transparent',
                borderRadius: '4px',
                margin: dropTargetIndex === index ? '4px 6px' : '2px 6px',
                transition: 'all 0.2s ease',
                opacity: isVisible ? 1 : 0,
                pointerEvents: 'auto',
                cursor: dropTargetIndex === index ? 'copy' : 'default',
            }}
        />
    );

    const getContiguousBlocks = () => {
        const blocks = [];
        let i = 0;

        while (i < steps.length) {
            if (steps[i].selected) {
                let start = i;
                while (i < steps.length && steps[i].selected) {
                    i++;
                }
                let end = i - 1;
                blocks.push({ start, end, length: end - start + 1 });
            } else {
                i++;
            }
        }

        return blocks;
    };

    const shouldShowBlockHandles = () => {
        const selectedIndices = steps.map((step, index) => step.selected ? index : -1).filter(i => i !== -1);
        const blocks = getContiguousBlocks();

        // Only show block handles if:
        // 1. There are selected items
        // 2. There's exactly ONE contiguous block
        // 3. All selected items are in that single block
        return selectedIndices.length > 1 && blocks.length === 1 && blocks[0].length === selectedIndices.length;
    };

    const onBlockDragStart = (e, block) => {
        const indices = Array.from({ length: block.length }, (_, i) => block.start + i);
        e.dataTransfer.effectAllowed = "move";
        try {
            e.dataTransfer.setData("text/plain", JSON.stringify({ indices, type: 'steps' }));
        } catch (_) {
            e.dataTransfer.setData("text", JSON.stringify({ indices, type: 'steps' }));
        }
        setIsDragging(true);
    };

    const BlockDragHandle = ({ block }) => {
        if (block.length <= 1 || !shouldShowBlockHandles()) return null;

        // Use refs to measure actual positions for perfect alignment
        const firstItemRef = itemRefs.current[block.start];
        const lastItemRef = itemRefs.current[block.end];

        let startOffset = 0;
        let totalHeight = 0;

        if (firstItemRef && lastItemRef) {
            // Measure actual positions relative to the scrollable container
            const scrollableContainer = firstItemRef.parentElement?.parentElement?.parentElement;
            if (scrollableContainer) {
                const containerRect = scrollableContainer.getBoundingClientRect();
                const firstRect = firstItemRef.getBoundingClientRect();
                const lastRect = lastItemRef.getBoundingClientRect();

                startOffset = firstRect.top - containerRect.top;
                const endOffset = lastRect.bottom - containerRect.top;
                totalHeight = endOffset - startOffset;
            }
        } else {
            // Fallback calculation if refs not available
            const fallbackRowHeight = 84;
            startOffset = block.start * fallbackRowHeight + 6;
            totalHeight = block.length * fallbackRowHeight - 12;
        }

        return (
            <div
                draggable
                onDragStart={(e) => onBlockDragStart(e, block)}
                style={{
                    position: 'absolute',
                    left: '2px',
                    top: `${startOffset}px`,
                    width: '16px',
                    height: `${totalHeight}px`,
                    background: 'linear-gradient(180deg, #1890ff, #52c41a)',
                    borderRadius: '8px',
                    cursor: 'grab',
                    boxShadow: '0 4px 12px rgba(24, 144, 255, 0.4)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    color: 'white',
                    fontWeight: 'bold',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                }}
                title={`Drag ${block.length} steps as a block`}
            >
                {block.length > 3 ? block.length : '⋮⋮'}
            </div>
        );
    };

    return (
        <div
            onClick={() => onOpenTrainingForm && onOpenTrainingForm()}
            style={fillHeight ? { height: "100%", display: "flex", minHeight: 0 } : undefined}
        >
            <div
                style={{
                    position: "relative",
                    padding: 12,
                    borderRadius: 18,
                    background: "radial-gradient(120% 120% at 0% 0%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 100%)",
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                    border: "1px solid rgba(255,255,255,0.28)",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.20)",
                    overflow: "hidden",
                    display: fillHeight ? "flex" : undefined,
                    flexDirection: fillHeight ? "column" : undefined,
                    height: fillHeight ? "100%" : undefined,
                    flex: fillHeight ? "1 1 auto" : undefined,
                    minHeight: fillHeight ? 0 : undefined,
                }}
                onDragOver={isDragging ? undefined : onContainerDragOver}
                onDrop={isDragging ? undefined : onContainerDrop}
            >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                    <Text>
                        {steps.length} steps, duration: <strong>{formatTime(totTimeFinal)}</strong>, Avg Power:{" "}
                        <strong>
                            {steps.length === 0 || !totTimeFinal
                                ? 0
                                : powerUnit === "percent"
                                    ? `${Math.round((totWatt / totTimeFinal) * 100 / ftpSafe)} % FTP`
                                    : `${Math.round(totWatt / totTimeFinal)} Watts`}
                        </strong>
                    </Text>
                    <div>
                        <Button type="link" onClick={() => toggleSelectStep(steps.map((x, i) => !x.selected ? i : -1))}>Select all</Button>
                        <Button type="link" onClick={() => toggleSelectStep(steps.map((x, i) => x.selected ? i : -1))}>Unselect all</Button>
                    </div>
                </div>
                <Row style={{ marginBottom: "10px", marginLeft: "10px" }}>Tip : Hold Shift to select multiple</Row>
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 12,
                        background: "linear-gradient(180deg, rgba(255,255,255,0.22), rgba(255,255,255,0))",
                        pointerEvents: "none",
                    }}
                />
                <div
                    style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: 16,
                        background: "linear-gradient(0deg, rgba(255,255,255,0.14), rgba(255,255,255,0))",
                        pointerEvents: "none",
                    }}
                />
                {steps.length === 0 && (
                    <div style={{ textAlign: "center", padding: "24px 0", color: "rgba(0,0,0,0.6)", cursor: "pointer" }}>
                        Click to create your plan
                    </div>
                )}
                <div
                    style={
                        fillHeight
                            ? { flex: "1 1 auto", minHeight: 0, overflowY: "auto", paddingRight: 6, position: "relative" }
                            : { maxHeight: "52vh", overflowY: "auto", paddingRight: 6, position: "relative" }
                    }
                >
                    {/* Block drag handles for contiguous selections */}
                    {shouldShowBlockHandles() && getContiguousBlocks().map((block, idx) => (
                        <BlockDragHandle key={`block-${idx}`} block={block} />
                    ))}

                    {/* Drop zone at the beginning */}
                    <DropZone index={0} isVisible={isDragging} />

                    <List
                        bordered={false}
                        split={false}
                        dataSource={steps}
                        renderItem={(step, index) => {
                            const base = getColor(step.watt);
                            const tintStrong = withAlpha(base, step.selected ? 0.26 : 0.16);
                            const tintSoft = withAlpha(base, 0.06);
                            const borderCol = withAlpha(base, step.selected ? 0.55 : 0.35);
                            const glow = step.selected ? `0 6px 22px ${withAlpha(base, 0.28)}` : "0 2px 12px rgba(0,0,0,0.12)";

                            // Check if this item is part of a contiguous block and block handles are shown
                            const showBlockHandles = shouldShowBlockHandles();
                            const blocks = getContiguousBlocks();
                            const isInBlock = showBlockHandles && blocks.some(block => index >= block.start && index <= block.end && block.length > 1);

                            return (
                                <div key={index} ref={(el) => itemRefs.current[index] = el}>
                                    <List.Item
                                        draggable={!isInBlock} // Disable individual dragging for items in blocks when block handles are shown
                                        onDragStart={!isInBlock ? (e) => onItemDragStart(e, index) : undefined}
                                        style={{
                                            background: `linear-gradient(180deg, ${tintStrong}, ${tintSoft})`,
                                            backdropFilter: "blur(12px)",
                                            WebkitBackdropFilter: "blur(12px)",
                                            border: `1px solid ${borderCol}`,
                                            borderRadius: 14,
                                            boxShadow: glow,
                                            margin: "4px 6px",
                                            padding: "10px 12px",
                                            transition: "all .18s ease",
                                            cursor: isInBlock ? "default" : "grab",
                                            title: isInBlock ? "Use the blue block handle to drag multiple steps" : "Drag to reorder this step",
                                            userSelect: "none",
                                            opacity: isInBlock ? 0.9 : 1,
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", width: "100%", gap: 10 }}>
                                            <div
                                                style={{
                                                    width: 8,
                                                    alignSelf: "stretch",
                                                    borderRadius: 8,
                                                    background: withAlpha(base, step.selected ? 0.7 : 0.5),
                                                }}
                                            />
                                            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "rgba(0,0,0,0.78)", flexWrap: "wrap" }}>
                                                <Checkbox
                                                    checked={step.selected || false}
                                                    onChange={() => toggleSelectStep(
                                                        isShiftPressed && steps.findIndex(s => s.selected) !== -1 && steps.findIndex(s => s.selected) !== index
                                                            ? (
                                                                steps.findIndex(s => s.selected) > index
                                                                    ?
                                                                    [index]
                                                                    :
                                                                    Array.from({ length: index - findLastSelected(steps, index) }, (_, i) => findLastSelected(steps, index) + i + 1))
                                                            : [index]
                                                    )}
                                                    style={{ marginRight: '10px' }}
                                                />
                                                <Text type="secondary" style={{ minWidth: 18, textAlign: "right" }}>
                                                    {index + 1}.
                                                </Text>
                                                <Text strong>{formatTime(step.duration)}</Text>
                                                <span>at</span>
                                                {powerUnit === "percent" ? (
                                                    <Text strong>{Math.round(step.watt * 100 / ftpSafe)} % FTP</Text>
                                                ) : (
                                                    <>
                                                        <Text strong>{Math.round(step.watt)} Watts</Text>
                                                        <span style={{ color: "rgba(0,0,0,0.55)" }}>
                                                            ({Math.round(step.watt * 100 / ftpSafe)} % FTP)
                                                        </span>
                                                    </>
                                                )}
                                                {step.comment && (
                                                    <span style={{ color: "rgba(0,0,0,0.55)" }}> {step.comment}</span>
                                                )}
                                            </div>
                                        </div>
                                    </List.Item>

                                    {/* Drop zone between items */}
                                    <DropZone index={index + 1} isVisible={isDragging} />
                                </div>
                            )
                        }}
                    />
                </div>
            </div>
        </div>
    );

}

export default TrainingList;
